// Single source of truth for PLP filter/category logic. Every rule here used to be
// duplicated across filters/index.vue, filters/mobile/index.vue, usePlpFilters,
// CurrentFilters.vue, Layout.vue and SearchCategoryTree.vue — change it here only.

export interface PlpFilter {
    code?: string;
    label?: string;
    id?: string;
    options?: any[];
    entities?: any[];
    min?: number;
    max?: number;
}

export function queryStr(val: unknown): string {
    if (Array.isArray(val)) return (val[0] as string) || '';
    return (val as string) || '';
}

// Filter type is decided by Shopware code OR the (Dutch) display label — the label
// match is load-bearing: after client-side filtering all codes become "properties".
export function isManufacturerFilter(filter: PlpFilter): boolean {
    return filter.code === 'manufacturer' || filter.label?.toLowerCase() === 'merk';
}

export function isPriceFilter(filter: PlpFilter): boolean {
    return filter.code === 'price' || filter.label?.toLowerCase() === 'prijs';
}

// The category facet is rendered as a tree elsewhere; the generic filter hides it.
export function isCategoryLabel(label?: string): boolean {
    return label?.toLowerCase() === 'categorie';
}

// Open/closed state key — label-based because filter codes and IDs change between
// API responses (initial: "manufacturer"/"price"; after filtering: all "properties").
export function getFilterKey(filter: PlpFilter): string {
    if (isManufacturerFilter(filter)) return 'manufacturer-filter';
    if (isPriceFilter(filter)) return 'price-filter';
    return filter.label?.toLowerCase() || filter.id || filter.code || '';
}

// Manufacturer filters carry entities and/or options depending on the API response shape.
export function getAllFilterItems(filter: PlpFilter): any[] {
    if (isManufacturerFilter(filter)) {
        return [...(filter.entities || []), ...(filter.options || [])];
    }
    return filter.options || [];
}

export function getPriceMinFromOptions(filter: PlpFilter): number {
    if (filter.min != null) return filter.min;
    if (!filter.options || filter.options.length === 0) return 0;
    const prices = filter.options
        .map((opt: any) => parseFloat(opt.translated?.name || opt.name || ''))
        .filter((n: number) => !isNaN(n));
    return prices.length > 0 ? Math.min(...prices) : 0;
}

export function getPriceMaxFromOptions(filter: PlpFilter): number {
    if (filter.max != null) return filter.max;
    if (!filter.options || filter.options.length === 0) return 1000;
    const prices = filter.options
        .map((opt: any) => parseFloat(opt.translated?.name || opt.name || ''))
        .filter((n: number) => !isNaN(n));
    return prices.length > 0 ? Math.max(...prices) : 1000;
}

export function reorderFilters(filters: PlpFilter[]): PlpFilter[] {
    if (!filters) return [];
    const filtersCopy = [...filters];
    const priceFilterIndex = filtersCopy.findIndex(filter => isPriceFilter(filter));
    if (priceFilterIndex !== -1) {
        const [priceFilter] = filtersCopy.splice(priceFilterIndex, 1);
        filtersCopy.push(priceFilter);
    }
    return filtersCopy;
}

// ?properties= holds ALL selected filter values as pipe-joined opaque hex IDs.
export function parsePropertyIds(raw: unknown): string[] {
    return typeof raw === 'string' ? raw.split('|').filter(Boolean) : [];
}

export function togglePropertyId(ids: string[], id: string): string[] {
    const criteria = [...ids];
    if (!criteria.includes(id)) {
        criteria.push(id);
    } else {
        criteria.splice(criteria.indexOf(id), 1);
    }
    return criteria;
}

// Nieuw/Sale are pseudo-categories injected into the tree by Tweakwise.
export function isSpecialCategoryName(name?: string): boolean {
    return name === 'nieuw' || name === 'new' || name === 'sale';
}

export function isSpecialCategoryItem(item: any): boolean {
    return isSpecialCategoryName(item?.title?.toLowerCase());
}

export function isSaleItem(item: any): boolean {
    return item?.title?.toLowerCase() === 'sale';
}

// Last segment of a tn_cid path ("a-b-c" → "c"), each segment 32 hex chars.
// This USED to be the Shopware category UUID, but the Tweakwise feed now writes
// categories as md5(categoryId_domainId), so on current data the tail is a hash
// that maps to nothing in Shopware. Only useful for pre-hash responses — prefer
// categoryIdOf().
export function extractCategoryUuid(categoryPath?: string): string | undefined {
    if (!categoryPath) return undefined;
    return categoryPath.match(/([0-9a-f]{32})$/)?.[1];
}

// The Shopware category of a tree node. The backend resolves the hash for us and
// hands it over as categoryId (NdTweakwise CategoryTreeEnricher); the path parse
// is only a fallback for responses that predate it.
export function categoryIdOf(item: any): string | undefined {
    return item?.categoryId ?? extractCategoryUuid(item?.categoryPath);
}

// A selected Sale/Nieuw node hands its children to the sidebar as the rendered list,
// so its children don't show as both nested items and root-level siblings.
export function renderedTreeItems(items: any[] | undefined): any[] {
    if (!items?.length) return [];
    const selectedSpecial = items.find((i: any) => i.isSelected && isSpecialCategoryItem(i));
    if (selectedSpecial?.children?.length) return selectedSpecial.children;
    return items;
}

// Search hides Shop and displays the children of the selected Nieuw/Sale branch.
// Share this projection with automatic selection so it counts the visible choices.
export function renderedSearchTreeItems(items: any[] | undefined): any[] {
    const withoutShop = (nodes: any[]): any[] => nodes
        .filter(item => item.title?.trim().toLowerCase() !== 'shop')
        .map(item => ({ ...item, children: withoutShop(item.children || []) }));
    return renderedTreeItems(withoutShop(items || []));
}

// Deepest selected node's OWN children (empty on a leaf — never the parent's siblings).
export function deepestSelectedChildren(items: any[] | undefined): any[] | null {
    if (!items?.length) return null;
    const selected = items.find((i: any) => i.isSelected);
    if (!selected) return null;
    const deeper = deepestSelectedChildren(selected.children);
    return deeper ?? selected.children ?? [];
}

// Sale/Nieuw variant, detected from the path, the ?variant= query (caller normalizes
// it to a string) or a selected special item in the category tree.
export function detectActiveVariant(
    path: string,
    queryVariant: string | undefined,
    treeItems?: any[],
): 'sale' | 'nieuw' | undefined {
    const segments = path.toLowerCase().split('/');
    if (segments.includes('sale')) return 'sale';
    if (segments.includes('nieuw')) return 'nieuw';
    if (queryVariant === 'sale' || queryVariant === 'nieuw') return queryVariant;
    const selected = (treeItems ?? []).find((i: any) => i.isSelected && isSpecialCategoryItem(i));
    const name = selected?.title?.toLowerCase();
    if (name === 'sale') return 'sale';
    if (name === 'nieuw' || name === 'new') return 'nieuw';
    return undefined;
}

// Resolve a manufacturer tree item to its SEO URL. Key scheme of seoMap (built in
// Layout.vue): categoryId / uuid → path, `variant:<categoryId|uuid>` → variant path,
// `variant:<sale|nieuw>` → the variant root.
export function resolveManufacturerSeoUrl(
    item: any,
    opts: { seoMap?: Record<string, string>; variant?: string; treeItems?: any[] },
): string | undefined {
    const { seoMap, variant, treeItems } = opts;
    if (!seoMap) return undefined;

    if (isSpecialCategoryItem(item)) {
        const name = item.title?.toLowerCase();
        const variantName = name === 'sale' ? 'sale' : 'nieuw';
        return seoMap[`variant:${variantName}`];
    }
    const uuid = categoryIdOf(item);

    if (variant) {
        if (item.categoryId && seoMap[`${variant}:${item.categoryId}`]) {
            return seoMap[`${variant}:${item.categoryId}`];
        }
        if (uuid && seoMap[`${variant}:${uuid}`]) return seoMap[`${variant}:${uuid}`];
        // Categories inside a Sale/Nieuw subtree carry their own ids, which for some brands
        // are absent from the SEO map (A-Dam) and for others happen to match (Barbara Lebek).
        // The variant keys are built from the MAIN category id, and that is the same-named
        // item at root level — so route through its twin.
        const title = item.title?.toLowerCase();
        const twin = (treeItems ?? []).find((i: any) => !isSpecialCategoryItem(i) && i.title?.toLowerCase() === title);
        const twinUuid = categoryIdOf(twin);
        if (twinUuid && seoMap[`${variant}:${twinUuid}`]) return seoMap[`${variant}:${twinUuid}`];
    }

    if (item.categoryId && seoMap[item.categoryId]) return seoMap[item.categoryId];
    if (uuid && seoMap[uuid]) return seoMap[uuid];
    return undefined;
}

// Which query params count as "the listing changed" — the grid resets and refetches
// only for these. A new filter param MUST be added here or filtering silently breaks.
export function haveListingFiltersChanged(newQuery: Record<string, any>, oldQuery: Record<string, any>): boolean {
    return (
        (newQuery.properties || undefined) !== (oldQuery.properties || undefined) ||
        (newQuery['min-price'] || undefined) !== (oldQuery['min-price'] || undefined) ||
        (newQuery['max-price'] || undefined) !== (oldQuery['max-price'] || undefined) ||
        (newQuery.sort || undefined) !== (oldQuery.sort || undefined) ||
        (newQuery.search || undefined) !== (oldQuery.search || undefined) ||
        (newQuery.tn_q || undefined) !== (oldQuery.tn_q || undefined) ||
        (newQuery.tn_cid || undefined) !== (oldQuery.tn_cid || undefined) ||
        (newQuery.newOnly || undefined) !== (oldQuery.newOnly || undefined)
    );
}
